import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import {
    ClientSubscriptionService,
    ClientSubscriptionType,
} from 'app/modules/settings/services/client-subscription.service';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ClientInformationInterface } from 'app/modules/shared/interfaces/client-data';
import { ClientsService } from 'app/modules/shared/services/clients.service';
import { ToastService } from 'app/modules/shared/services/toast.service';

@Component({
    selector: 'client-edit-details',
    templateUrl: './client-edit-details.component.html',
    styleUrls: ['../../client-edit-screen.component.scss', './client-edit-details.component.scss'],
})
export class ClientEditDetailsComponent implements OnInit {
    initControlsFlag: boolean = false;
    subscriptionTypeListCopy: ClientSubscriptionType[];
    subscriptionTypeList: ClientSubscriptionType[];

    @Input() form: UntypedFormGroup;
    @Input() clientModel: ClientInformationInterface;
    @Input() action: string;
    @Output() formGroupEvent = new EventEmitter<UntypedFormGroup>();

    clientDetails: UntypedFormGroup;
    clientKey: UntypedFormControl;
    fullName: UntypedFormControl;
    shortName: UntypedFormControl;
    hubSpotUrl: UntypedFormControl;
    compenSafeUrl: UntypedFormControl;
    contractDate: UntypedFormControl;
    goLiveDate: UntypedFormControl;
    loanSeedDate: UntypedFormControl;
    cS_CompanyID: UntypedFormControl;
    use_Hourly_KPI: UntypedFormControl;
    clientSubscriptionTypeKey: UntypedFormControl;
    useSSO: UntypedFormControl;
    companyURL: UntypedFormControl;
    is_nvlite: UntypedFormControl;
    isLoginDisabled: UntypedFormControl;
    isActive: UntypedFormControl;
    dataSwapKey: string;
    entityClassificationList: Array<string>;
    entityClassification: UntypedFormControl;
    customerNumber: UntypedFormControl;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private clientsService: ClientsService,
        private reusableFunctions: ReusableFunctions,
        private toastService: ToastService,
        private clientSubscriptionService: ClientSubscriptionService
    ) {}

    ngOnInit() {
        this.getPageData();
        this.constructGroup();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Seems like this part is not hitting due to 'OnChanges' has not implemented on the header.
        // So this function can be removed.
        setTimeout(() => {
            if (changes['form']?.currentValue?.controls?.hasOwnProperty('clientDetails')) {
                this.initControls();
                if (this.action != 'Add') {
                    this.patchValues();
                    this.formatDates();
                }
                this.setDefaults();
                this.getAndSetSuggestedClientKey();
                this.initControlsFlag = true;
            }
        }, 0);
    }

    constructGroup() {
        let group: UntypedFormGroup = this.formBuilder.group({
            clientKey: ['', Validators.required],
            fullName: ['', [Validators.required, Validators.maxLength(100)]],
            shortName: ['', [Validators.required, Validators.maxLength(50)]],
            companyURL: ['', Validators.maxLength(200)],
            clientSubscriptionTypeKey: ['', Validators.required],
            contractDate: ['', Validators.required],
            goLiveDate: [''],
            loanSeedDate: ['', Validators.required],
            cS_CompanyID: [''],
            use_Hourly_KPI: [''],
            is_nvlite: ['false'],
            useSSO: ['true'],
            hubSpotUrl: ['', Validators.maxLength(150)],
            compenSafeUrl: ['', Validators.maxLength(150)],
            isLoginDisabled: ['false'],
            isActive: ['false'],
            entityClassification: ['', [Validators.required]],
            customerNumber: [''],
        });

        this.formGroupEvent.emit(group);
    }

    getPageData() {
        this.entityClassificationList = ['IMB', 'Bank', 'Credit Union', ''];
        this.clientSubscriptionService.getSubscriptionTypes().subscribe(
            (data) => {
                this.subscriptionTypeListCopy = data;
                this.subscriptionTypeList = this.setAvailableSubscriptionTypes();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    setAvailableSubscriptionTypes(): ClientSubscriptionType[] {
        if (this.is_nvlite.value == 'true') {
            return this.subscriptionTypeListCopy.filter((x) => x.nvLite == true);
        }

        return this.subscriptionTypeListCopy;
    }

    updateNvLiteSettings(event) {
        this.subscriptionTypeList = this.setAvailableSubscriptionTypes();
        let firstSubscriptionInList = this.subscriptionTypeList[0];
        this.clientSubscriptionTypeKey.setValue(
            firstSubscriptionInList.clientSubscriptionTypeKey.toString()
        );
        //this.clientModel.clientDetails.nvLite
        this.formGroupEvent.emit(this.clientDetails);
    }

    initControls() {
        this.clientDetails = this.form.get('clientDetails') as UntypedFormGroup;
        this.clientKey = this.clientDetails.get('clientKey') as UntypedFormControl;
        this.fullName = this.clientDetails.get('fullName') as UntypedFormControl;
        this.shortName = this.clientDetails.get('shortName') as UntypedFormControl;
        this.hubSpotUrl = this.clientDetails.get('hubSpotUrl') as UntypedFormControl;
        this.compenSafeUrl = this.clientDetails.get('compenSafeUrl') as UntypedFormControl;
        this.contractDate = this.clientDetails.get('contractDate') as UntypedFormControl;
        this.goLiveDate = this.clientDetails.get('goLiveDate') as UntypedFormControl;
        this.loanSeedDate = this.clientDetails.get('loanSeedDate') as UntypedFormControl;
        this.cS_CompanyID = this.clientDetails.get('cS_CompanyID') as UntypedFormControl;
        this.use_Hourly_KPI = this.clientDetails.get('use_Hourly_KPI') as UntypedFormControl;
        this.is_nvlite = this.clientDetails.get('is_nvlite') as UntypedFormControl;
        this.clientSubscriptionTypeKey = this.clientDetails.get(
            'clientSubscriptionTypeKey'
        ) as UntypedFormControl;
        this.useSSO = this.clientDetails.get('useSSO') as UntypedFormControl;
        this.companyURL = this.clientDetails.get('companyURL') as UntypedFormControl;
        this.isLoginDisabled = this.clientDetails.get('isLoginDisabled') as UntypedFormControl;
        this.isActive = this.clientDetails.get('isActive') as UntypedFormControl;
        this.entityClassification = this.clientDetails.get(
            'entityClassification'
        ) as UntypedFormControl;
        this.customerNumber = this.clientDetails.get('customerNumber') as UntypedFormControl;

        if (this.clientModel?.clientDetails?.lgDataSwapKey) {
            this.dataSwapKey = this.clientModel.clientDetails.lgDataSwapKey;
        } else {
            this.dataSwapKey = '';
        }
    }

    setDefaults() {
        this.setDefaultAuth();
        this.setDefaultHourlyKPI();
        this.setIsNvLite();
        this.setDefaultActiveStatus();
    }

    patchValues() {
        this.clientDetails.patchValue(this.clientModel.clientDetails);
    }

    /* Fetch the next available clientKey & set the form if action is "Add" */
    getAndSetSuggestedClientKey() {
        if (this.action == 'Add') {
            this.clientsService.getSuggestedClientKey().subscribe((res) => {
                if (res && res.suggestedKey) {
                    this.clientKey.setValue(res.suggestedKey);
                }
            });
        }
    }

    setDefaultAuth() {
        if (this.action == 'Add') {
            this.useSSO.setValue('false');
        } else if (this.clientModel.clientDetails.useSSO == null) {
            this.useSSO.setValue('Both');
        } else {
            this.useSSO.setValue(this.clientModel.clientDetails.useSSO.toString());
        }
    }

    setDefaultHourlyKPI() {
        if (
            !this.clientModel.clientDetails ||
            this.clientModel.clientDetails.use_Hourly_KPI == null
        ) {
            this.use_Hourly_KPI.setValue('false');
        } else {
            this.use_Hourly_KPI.setValue(this.clientModel.clientDetails.use_Hourly_KPI.toString());
        }
    }

    setIsNvLite() {
        if (this.action == 'Add') {
            this.is_nvlite.setValue('false');
        } else if (this.clientModel.clientDetails.nvLite == null) {
            this.is_nvlite.setValue('false');
        } else {
            this.is_nvlite.setValue(this.clientModel.clientDetails.nvLite.toString());
        }
    }

    setDefaultActiveStatus() {
        if (this.action == 'Add') {
            this.isActive.setValue(true);
            this.isLoginDisabled.setValue(false);
        } else {
            this.isActive.setValue(this.clientModel.clientDetails.isActive);
            this.isLoginDisabled.setValue(this.clientModel.clientDetails.isLoginDisabled);
        }
    }

    /* Date Formatting */
    formatDates() {
        let contractDate: Date = this.clientModel.clientDetails.contractDate;
        let goLiveDate: Date = this.clientModel.clientDetails.goLiveDate;
        let loanSeedDate: Date = this.clientModel.clientDetails.loanSeedDate;

        if (contractDate) {
            contractDate = this.reusableFunctions.getDateFromDateTimeOffset(contractDate);
            this.clientModel.clientDetails.contractDate = contractDate;
            this.contractDate.setValue(contractDate);
        }

        if (goLiveDate) {
            goLiveDate = this.reusableFunctions.getDateFromDateTimeOffset(goLiveDate);
            this.clientModel.clientDetails.goLiveDate = goLiveDate;
            this.goLiveDate.setValue(goLiveDate);
        }

        if (loanSeedDate) {
            loanSeedDate = this.reusableFunctions.getDateFromDateTimeOffset(loanSeedDate);
            this.clientModel.clientDetails.loanSeedDate = loanSeedDate;
            this.loanSeedDate.setValue(loanSeedDate);
        }
    }

    updateShortName() {
        let clientShortName = this.shortName.value;
        this.clientsService.isClientShortNameUnique(clientShortName).subscribe(
            (data) => {
                if (data == true) {
                    this.shortName.setErrors({ shortNameNotUnique: true });
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    updateClientKey() {
        let clientKey = this.clientKey.value;
        this.clientsService.isClientKeyUnique(clientKey).subscribe(
            (data) => {
                if (data == false) {
                    this.clientKey.setErrors({ clientKeyNotUnique: true });
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    clickDisableAllUsers(event) {
        // this.toastService.resetAll();
        // if (event?.checked) {
        //     this.toastService.warning(
        //         'Login will be disabled for: ' + this.fullName.value,
        //         { dismiss: true, timeout: 4000 }
        //     );
        // } else {
        //     this.toastService.warning(
        //         'Login will be enabled for: ' + this.fullName.value,
        //         { dismiss: true, timeout: 4000 }
        //     );
        // }
    }
}
